/**
 * <StudentSettingsAddressPage> — « Adresse », la deuxième page des paramètres de l'élève (page).
 *
 * Les cinq champs de l'onglet d'aujourd'hui, tous modifiables par l'élève : l'adresse, son
 * complément, le code postal, la ville, et le pays dans une feuille de choix. Le code postal se
 * valide selon le pays : son erreur arrive en props, sous le champ.
 */
import React from "react"

import { Stack } from "../../atoms/containers/Stack"
import { TextInput } from "../../atoms/TextInput"
import { Card } from "../../molecules/Card"
import { SelectField, type UISelectOption } from "../../molecules/SelectField"
import {
    AccountFormCardSkeleton,
    AccountSettingsTemplate,
    type AccountSettingsShellProps,
} from "../../templates/AccountSettingsTemplate"
import { type UIStudentAddressSettings } from "../../../ports/primary/ui/types"

export type { UIStudentAddressSettings }

export type StudentAddressSettingsField = keyof UIStudentAddressSettings

export type StudentSettingsAddressPageProps = AccountSettingsShellProps & {
    /** `null` tant que le compte n'est pas là. */
    values: UIStudentAddressSettings | null
    errors?: Partial<Record<StudentAddressSettingsField, string>>
    onChange: (field: StudentAddressSettingsField, value: string) => void
    countries: UISelectOption[]
    /** Ouvre la feuille des pays au premier rendu (stories). */
    countrySheetOpen?: boolean
}

export function StudentSettingsAddressPage(props: StudentSettingsAddressPageProps) {
    const { values, errors = {}, onChange, countries, countrySheetOpen = false, ...shell } = props

    const busy = shell.submitting === true
    const field = (name: StudentAddressSettingsField) => ({
        value: values?.[name] ?? "",
        onChangeText: (text: string) => onChange(name, text),
        error: errors[name],
        disabled: busy,
    })

    return (
        <AccountSettingsTemplate {...shell} title="Adresse" skeleton={<AccountFormCardSkeleton fields={5} />}>
            {values != null && (
                <Card p="4">
                    <Stack gap="3">
                        <TextInput
                            {...field("address")}
                            label="Adresse"
                            placeholder="ex : 12 rue de la Paix"
                            textContentType="streetAddressLine1"
                            returnKeyType="next"
                        />
                        <TextInput
                            {...field("addressComplement")}
                            label="Complément d'adresse"
                            placeholder="Bâtiment, étage, etc."
                            textContentType="streetAddressLine2"
                            returnKeyType="next"
                        />
                        <TextInput
                            {...field("postalCode")}
                            label="Code postal"
                            placeholder="ex : 75001"
                            textContentType="postalCode"
                            returnKeyType="next"
                        />
                        <TextInput
                            {...field("city")}
                            label="Ville"
                            placeholder="ex : Paris"
                            textContentType="addressCity"
                        />
                        <SelectField
                            label="Pays"
                            items={countries}
                            getItemId={(country) => country.id}
                            getItemLabel={(country) => country.label}
                            selectedIds={values.countryId !== "" ? [values.countryId] : []}
                            onChange={(ids) => onChange("countryId", String(ids[0] ?? ""))}
                            searchPlaceholder="Rechercher un pays"
                            error={errors.countryId}
                            disabled={busy}
                            defaultOpen={countrySheetOpen}
                        />
                    </Stack>
                </Card>
            )}
        </AccountSettingsTemplate>
    )
}
